import React, { useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, Empty } from "@/components/app/Primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, Circle, GraduationCap, Award, PlayCircle } from "lucide-react";

export default function MyLearning() {
    const [mine, setMine] = useState([]);
    const [cat, setCat] = useState([]);
    const [active, setActive] = useState(null); // {enrollment, course}
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);

    const load = () => {
        api.get("/learning/me").then((r) => setMine(r.data.items || [])).catch(() => {});
        api.get("/learning/catalog").then((r) => setCat(r.data.items || [])).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const openCourse = async (enrollment_id) => {
        setResult(null); setAnswers({});
        const { data } = await api.get(`/learning/me/enrollments/${enrollment_id}`);
        setActive(data);
    };
    const enroll = async (course_id) => {
        try { const { data } = await api.post(`/learning/catalog/${course_id}/enroll`); load(); openCourse(data.enrollment_id); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const markLesson = async (lesson_id) => {
        try { const { data } = await api.post(`/learning/me/enrollments/${active.enrollment.id}/complete-lesson`, { lesson_id }); setActive(data); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const submitQuiz = async () => {
        try {
            const { data } = await api.post(`/learning/me/enrollments/${active.enrollment.id}/quiz`, { answers });
            setResult(data);
            if (data.completed) toast.success("Course completed! 🎓");
            else if (!data.passed) toast.error(`Score ${data.score}% — didn’t reach the pass mark. Try again.`);
            load(); openCourse(active.enrollment.id);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    // ---- Player view ----
    if (active) {
        const c = active.course, e = active.enrollment;
        const done = new Set(e.completed_lessons || []);
        const allLessonsDone = (c.lessons || []).every((l) => done.has(l.id));
        return (
            <div>
                <button onClick={() => { setActive(null); load(); }} className="text-sm text-muted-foreground mb-3">← Back to My Learning</button>
                <Surface className="p-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <div className="flex items-center gap-2"><GraduationCap className="size-5 text-primary" /><h2 className="font-display text-xl font-bold">{c.title}</h2></div>
                            <div className="text-sm text-muted-foreground">{c.category} · {c.description}</div>
                        </div>
                        {e.status === "completed" && <span className="badge badge-approved inline-flex items-center gap-1"><Award className="size-3" /> Completed · {e.certificate_no}</span>}
                    </div>

                    <div className="mt-5 space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lessons</div>
                        {(c.lessons || []).map((l, i) => (
                            <div key={l.id} className="rounded-md border p-3">
                                <div className="flex items-start gap-3">
                                    <button onClick={() => markLesson(l.id)} title="Mark complete">
                                        {done.has(l.id) ? <CheckCircle2 className="size-5 text-emerald-600" /> : <Circle className="size-5 text-muted-foreground" />}
                                    </button>
                                    <div className="flex-1">
                                        <div className="font-medium text-sm">{i + 1}. {l.title}{l.duration_min ? <span className="text-muted-foreground font-normal"> · {l.duration_min} min</span> : null}</div>
                                        {l.content && <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{l.content}</div>}
                                        {!done.has(l.id) && <button onClick={() => markLesson(l.id)} className="mt-2 text-xs text-primary">Mark as complete</button>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {c.quiz && (
                        <div className="mt-6 border-t pt-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Quiz · pass mark {c.quiz.pass_pct}%</div>
                            {!allLessonsDone && <div className="text-sm text-muted-foreground">Finish all lessons to unlock the quiz.</div>}
                            {allLessonsDone && (
                                <div className="space-y-3">
                                    {c.quiz.questions.map((q, i) => (
                                        <div key={q.id}>
                                            <div className="text-sm font-medium">{i + 1}. {q.q}</div>
                                            <div className="mt-1 space-y-1">
                                                {q.choices.map((ch, ci) => (
                                                    <label key={ci} className="flex items-center gap-2 text-sm">
                                                        <input type="radio" name={q.id} checked={answers[q.id] === ci} onChange={() => setAnswers((a) => ({ ...a, [q.id]: ci }))} /> {ch}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {result && <div className={`text-sm font-medium ${result.passed ? "text-emerald-600" : "text-destructive"}`}>Score: {result.score}% ({result.correct}/{result.total}) — {result.passed ? "passed" : "not yet"}</div>}
                                    <button onClick={submitQuiz} disabled={Object.keys(answers).length < c.quiz.questions.length} className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium disabled:opacity-50" data-testid="submit-quiz">Submit quiz</button>
                                </div>
                            )}
                        </div>
                    )}
                </Surface>
            </div>
        );
    }

    return (
        <div>
            <PageHeader title="My Learning" subtitle="Courses assigned to you and the catalog you can join." />
            <Tabs defaultValue="mine">
                <TabsList>
                    <TabsTrigger value="mine" data-testid="tab-mine">My Courses{mine.length ? ` (${mine.length})` : ""}</TabsTrigger>
                    <TabsTrigger value="catalog" data-testid="tab-catalog">Catalog</TabsTrigger>
                </TabsList>
                <TabsContent value="mine">
                    {!mine.length && <Surface className="p-6"><Empty title="No courses assigned" subtitle="Check the Catalog to enroll in available courses." /></Surface>}
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {mine.map((m) => (
                            <Surface key={m.enrollment_id} className="p-4 flex flex-col" data-testid={`my-course-${m.course_id}`}>
                                <div className="flex items-center justify-between"><span className="badge badge-info">{m.category}</span>
                                    {m.status === "completed" ? <span className="badge badge-approved">completed</span> : m.status === "in_progress" ? <span className="badge badge-pending">in progress</span> : <span className="badge badge-draft">not started</span>}</div>
                                <div className="font-display font-semibold mt-2 flex-1">{m.title}</div>
                                <div className="mt-2">
                                    <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${m.progress_pct}%` }} /></div>
                                    <div className="text-xs text-muted-foreground mt-1">{m.lessons_done}/{m.lessons_total} lessons{m.has_quiz ? (m.quiz_passed ? " · quiz passed" : " · quiz") : ""}</div>
                                </div>
                                <button onClick={() => openCourse(m.enrollment_id)} className="mt-3 h-9 rounded-md bg-primary text-white text-sm font-medium inline-flex items-center justify-center gap-2" data-testid={`open-${m.course_id}`}>
                                    <PlayCircle className="size-4" /> {m.status === "completed" ? "Review" : "Continue"}
                                </button>
                            </Surface>
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="catalog">
                    {!cat.length && <Surface className="p-6"><Empty title="No published courses" /></Surface>}
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {cat.map((c) => (
                            <Surface key={c.course_id} className="p-4 flex flex-col">
                                <span className="badge badge-info w-fit">{c.category}</span>
                                <div className="font-display font-semibold mt-2">{c.title}</div>
                                <div className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{c.description || "—"}</div>
                                <div className="text-xs text-muted-foreground mt-2">{c.lesson_count} lessons{c.has_quiz ? " · quiz" : ""}</div>
                                {c.enrolled
                                    ? <span className="mt-3 text-xs text-emerald-600">✓ Already enrolled</span>
                                    : <button onClick={() => enroll(c.course_id)} className="mt-3 h-9 rounded-md border text-sm font-medium">Enroll</button>}
                            </Surface>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
